import { useState } from "react";
import { useForm } from "react-hook-form";
import { PairSimProfileSchema, type PairSimProfile } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill } from "@/components/ui/pill";

// Perfil de simulação AUTORADO para UM par máquina×produto. É o que faz
// produtos diferentes simularem diferente na mesma máquina: o valor daqui entra na base da
// resolução (antes dos overrides do cenário) e vence a premissa/similar, nunca o medido.
// O ciclo NÃO é campo — é derivado do ritmo (3600/ritmo); exibimos o derivado como apoio.
// O setup entrou aqui (e não em Produto): a mesma troca custa 20 min na empacotadora
// e 0 na lavadora; um default por produto achataria justamente a diferença que decide a fila.

type PairFormValues = Partial<Record<PairField, number>>;
type PairField = "feedRateUnH" | "mtbfMin" | "mttrMin" | "qualityTargetPct" | "setupMinutes";

const FIELDS: Array<{ name: PairField; label: string; step: string; hint: string }> = [
  { name: "feedRateUnH", label: "Ritmo (un/h)", step: "1", hint: "peças por hora neste produto" },
  { name: "mtbfMin", label: "MTBF (min)", step: "1", hint: "tempo médio entre falhas" },
  { name: "mttrMin", label: "MTTR (min)", step: "1", hint: "tempo médio de reparo" },
  { name: "qualityTargetPct", label: "Qualidade (%)", step: "0.1", hint: "meta de aproveitamento" },
  // Custo de PREPARAR a máquina para este produto. Não é taxa contínua: o motor
  // o cobra na TROCA de produto ao simular a fila; é o que faz agrupar lotes valer a pena.
  { name: "setupMinutes", label: "Setup (min)", step: "1", hint: "troca para este produto" },
];

const FIELD_NAMES = FIELDS.map((f) => f.name);

interface Props {
  profile: PairSimProfile | null;
  readOnly: boolean;
  saving: boolean;
  onSave: (next: PairSimProfile | null) => void;
}

export function PairProfileFields({ profile, readOnly, saving, onSave }: Props) {
  const [error, setError] = useState<string | null>(null);
  const form = useForm<PairFormValues>({
    // Numéricos nascem `undefined` (nunca 0): 0 literal obrigaria apagar antes de digitar.
    defaultValues: {
      feedRateUnH: profile?.feedRateUnH,
      mtbfMin: profile?.mtbfMin,
      mttrMin: profile?.mttrMin,
      qualityTargetPct: profile?.qualityTargetPct,
      setupMinutes: profile?.setupMinutes,
    },
  });

  const feed = form.watch("feedRateUnH");
  const derivedCycle = typeof feed === "number" && feed > 0 ? 3600 / feed : null;

  function submit(values: PairFormValues) {
    // Preserva campos autorados que este editor ainda não exponha.
    const next: Record<string, number> = { ...(profile ?? {}) };
    for (const name of FIELD_NAMES) {
      const value = values[name];
      if (typeof value === "number" && Number.isFinite(value)) next[name] = value;
      else delete next[name];
    }
    const parsed = PairSimProfileSchema.safeParse(next);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Valor inválido.");
      return;
    }
    setError(null);
    // Nenhum campo preenchido ⇒ o par volta a usar os parâmetros da máquina (limpa o perfil).
    onSave(Object.keys(parsed.data).length > 0 ? parsed.data : null);
  }

  if (readOnly) {
    const authored = FIELDS.filter((f) => typeof profile?.[f.name] === "number");
    if (authored.length === 0) {
      return <p className="text-xs text-muted-foreground">Sem perfil próprio para este produto.</p>;
    }
    return (
      <dl className="grid grid-cols-2 gap-2 text-xs">
        {authored.map((f) => (
          <div key={f.name}>
            <dt className="text-muted-foreground">{f.label}</dt>
            <dd className="font-medium tabular-nums">{profile?.[f.name]}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <form onSubmit={(e) => void form.handleSubmit(submit)(e)} className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map((f) => (
          <div key={f.name} className="space-y-1">
            <Label htmlFor={`pair-${f.name}`} className="text-[11px]">
              {f.label}
            </Label>
            <Input
              id={`pair-${f.name}`}
              type="number"
              step={f.step}
              min="0"
              placeholder="0"
              className="h-8 text-sm"
              aria-describedby={`pair-${f.name}-hint`}
              {...form.register(f.name, { valueAsNumber: true })}
            />
            <p id={`pair-${f.name}-hint`} className="text-[10px] text-muted-foreground">
              {f.hint}
            </p>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground">
        {derivedCycle
          ? `Tempo de ciclo derivado: ${derivedCycle.toFixed(2)} s/un.`
          : "Campo vazio usa o parâmetro da própria máquina."}
      </p>

      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? "Salvando…" : "Salvar perfil"}
        </Button>
        <Pill tone="neutral">autorado</Pill>
      </div>
    </form>
  );
}
